// -------------------------------------------- IMPORT FILES START --------------------------------------------------------------
import $api from "../Api";
import ResponseChecker from "../Middlewares/ResponseChecker";
import { defaultMessages } from "../enums/responseMessages.js";

import Endpoints from "../Endpoints";
// -------------------------------------------- IMPORT FILES ENDED --------------------------------------------------------------

// ## ShoppingBasket GetBasketList
/**
 * Müşteri sepeti (normal kalemler + kural sepetleri).
 *
 * @param {{ params: { customerId: string }, query?: object }} option
 * @returns {Promise<{ message: string, data?: { id: string, basketItems: Array, totalPrice: number, totalExpense?: number, balance: number, ruleBaskets: Array, saleId?: string } }>}
 */
export const GetBasketList = async (option) => {
  const response = await $api.get(
    Endpoints.ShoppingBasket.GetBasketList(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

// ## ShoppingBasket RemoveBasketItem
/**
 * @param {{ customerId: string, productId: string, guid: string }} form
 */
export const RemoveBasketItem = async (form) => {
  const response = await $api.post(
    Endpoints.ShoppingBasket.RemoveBasketItem({ params: {}, query: {} }),
    form
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

// ## ShoppingBasket RemoveRuleBasket
/**
 * @param {{ customerId: string, extraRuleId: string }} form
 */
export const RemoveRuleBasket = async (form) => {
  const response = await $api.post(
    Endpoints.ShoppingBasket.RemoveRuleBasket({ params: {}, query: {} }),
    form
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

// ## ShoppingBasket Delete (sepeti tamamen sil)
/**
 * @param {{ params: { id: string }, query?: object }} option
 */
export const Delete = async (option) => {
  const response = await $api.delete(Endpoints.ShoppingBasket.Delete(option));
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

// ## ShoppingBasket Create (sepete ürün ekle)
/**
 * Paket ürünlerde opsiyonel `packageItems: [{ productId, quantity }]` gönderilebilir
 * (quantity = paket başına bileşen miktarı; kısmi gönderimde eksikler katalogdan tamamlanır).
 *
 * @param {{ customerId: string, productId: string, quantity: number, price?: number, costPrice?: number, isOnlyExtra?: boolean, isExpense?: boolean, packageItems?: Array<{ productId: string, quantity: number }> }} form
 */
export const Create = async (form) => {
  const cost = Number(form.costPrice);
  const body = {
    ...form,
    costPrice: Number.isFinite(cost) && cost >= 0 ? cost : 0,
    isOnlyExtra:
      form.isOnlyExtra === undefined || form.isOnlyExtra === null
        ? false
        : !!form.isOnlyExtra,
    isExpense:
      form.isExpense === undefined || form.isExpense === null
        ? false
        : !!form.isExpense,
  };
  const response = await $api.post(
    Endpoints.ShoppingBasket.Create({ params: {}, query: {} }),
    body
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

// ## ShoppingBasket AddQuantity (sepetteki ürün adedini güncelle — body’de hedef toplam adet)
/**
 * `packageItems` gönderilirse paket bileşen miktarları da yenilenir; gönderilmezse
 * mevcut satışa özel snapshot korunur.
 *
 * @param {{ customerId: string, productId: string, quantity: number, price?: number, costPrice?: number, isOnlyExtra?: boolean, isExpense?: boolean, packageItems?: Array<{ productId: string, quantity: number }> }} form
 */
export const AddQuantity = async (form) => {
  const cost = Number(form.costPrice);
  const body = {
    ...form,
    costPrice: Number.isFinite(cost) && cost >= 0 ? cost : 0,
    isOnlyExtra:
      form.isOnlyExtra === undefined || form.isOnlyExtra === null
        ? false
        : !!form.isOnlyExtra,
    isExpense:
      form.isExpense === undefined || form.isExpense === null
        ? false
        : !!form.isExpense,
  };
  const response = await $api.post(
    Endpoints.ShoppingBasket.AddQuantity({ params: {}, query: {} }),
    body
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

// ## ShoppingBasket GetBasketProductsWithRule (kampanya kuralına göre sepet ürün adayları)
/**
 * @param {{ params?: object, query: { extraRuleId: string, customerId: string } }} option
 */
export const GetBasketProductsWithRule = async (option) => {
  const response = await $api.get(
    Endpoints.ShoppingBasket.GetBasketProductsWithRule(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

// ## ShoppingBasket GetProductsByRule (kural kataloğu ürünleri)
/**
 * @param {{ params?: object, query: { extraRuleId: string, customerId: string } }} option
 */
export const GetProductsByRule = async (option) => {
  const response = await $api.get(
    Endpoints.ShoppingBasket.GetProductsByRule(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

// ## ShoppingBasket GetExtraProductsByRule
/**
 * Kampanya kuralına göre ücretsiz / ödül ürün adayları.
 *
 * @param {{ params?: object, query: { extraRuleId: string, customerId: string, searchText?: string } }} option
 */
export const GetExtraProductsByRule = async (option) => {
  const response = await $api.get(
    Endpoints.ShoppingBasket.GetExtraProductsByRule(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

// ## ShoppingBasket AddExtraRuleBasket (kampanya kuralı sepeti — ödül kalemleri)
/**
 * @param {{ customerId: string, extraRuleId: string, ruleBasketItems: Array<{ productId: string, guid: string, isExtraProduct: boolean }> }} form
 */
export const AddExtraRuleBasket = async (form) => {
  const response = await $api.post(
    Endpoints.ShoppingBasket.AddExtraRuleBasket({ params: {}, query: {} }),
    form
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

// ## ShoppingBasket AddOneTimeProduct (katalog dışı ek satır)
/**
 * @param {{ customerId: string, productName: string, unitPrice: number, costPrice?: number, vatRate?: number, quantity: number, applyDiscount: boolean, isExpense?: boolean }} form
 */
export const AddOneTimeProduct = async (form) => {
  const body = {
    ...form,
    isExpense:
      form.isExpense === undefined || form.isExpense === null
        ? false
        : !!form.isExpense,
  };
  const response = await $api.post(
    Endpoints.ShoppingBasket.AddOneTimeProduct({ params: {}, query: {} }),
    body
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

export default {
  GetBasketList,
  Create,
  AddQuantity,
  RemoveBasketItem,
  RemoveRuleBasket,
  Delete,
  GetBasketProductsWithRule,
  GetProductsByRule,
  GetExtraProductsByRule,
  AddExtraRuleBasket,
  AddOneTimeProduct,
};
